import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaEye, FaSpinner, FaTimes, FaMapMarkerAlt, FaTimesCircle } from 'react-icons/fa';
import { orderService } from '../../../../services/apiService';
import { toast } from 'react-toastify';

const ORDER_STATUSES = [
  'Pending Payment',
  'Confirmed',
  'Processing Item',
  'Packed',
  'Handed to Courier',
  'Shipment In Transit',
  'Delivery Completed',
  'Cancelled',
  'Returned'
];

const Orders = () => {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get('status') || 'all';
    return s?.toLowerCase() === 'cancelled' ? 'Cancelled' : s;
  });

  const fetchOrders = async () => {
    try {
      setError(null);
      const result = await orderService.getAllOrders({}, 0, 100);
      const list = result?.orders ?? [];
      setOrders(list.map(order => ({
        id: order._id,
        orderId: order.orderId,
        customer: order.userId?.name || 'Unknown',
        amount: order.total,
        status: order.status || 'Pending Payment',
        date: order.createdAt,
        items: order.items?.length || 0,
        payment: order.paymentStatus,
        cancellationReason: order.cancellationReason,
        cancelledDate: order.cancelledDate,
        cancelledItems: order.cancelledItems || [],
        shippingAddress: order.shippingAddress,
        orderItems: order.items || [],
        raw: order
      })));
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err?.message || 'Failed to fetch orders');
      toast.error(err?.message || 'Failed to fetch orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const s = searchParams.get('status');
    if (s?.toLowerCase() === 'cancelled') setStatusFilter('Cancelled');
  }, [searchParams]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filteredOrders = statusFilter === 'all' || !statusFilter
    ? orders
    : statusFilter === 'Cancelled'
      ? orders.filter(o => o.status === 'Cancelled' || (o.cancelledItems?.length > 0))
      : orders.filter(o => o.status === statusFilter);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedOrder(null);
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <FaSpinner className="animate-spin text-4xl text-purple-500" />
        <span className="ml-3 text-gray-600">Loading orders...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Orders</h2>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}
      <div className="flex gap-2 items-center mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
        >
          <option value="all">All Status</option>
          {ORDER_STATUSES.map(s => (<option key={s} value={s}>{s}</option>))}
        </select>
      </div>
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 text-gray-600">No orders found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Order ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Items</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{order.orderId}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{order.customer}</td>
                  <td className="px-4 py-3 text-sm font-semibold">₹{(order.amount || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{order.items}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      order.status === 'Delivery Completed' ? 'bg-green-100 text-green-800' :
                      order.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                      order.status === 'Processing Item' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                    }`}>{order.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(order.date)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleViewOrder(order)} className="text-purple-600 hover:text-purple-800 p-1" title="View Details">
                      <FaEye />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Order Details</h2>
                <p className="text-purple-100 text-sm mt-1">Order ID: {selectedOrder.orderId}</p>
              </div>
              <button onClick={closeDetailModal} className="text-white hover:bg-purple-700 p-2 rounded-lg"><FaTimes size={24} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Customer</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.customer}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Order Date</p>
                  <p className="font-semibold text-gray-900">{formatDate(selectedOrder.date)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Amount</p>
                  <p className="font-semibold text-gray-900">₹{(selectedOrder.amount || 0).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.status}</p>
                </div>
              </div>
              {selectedOrder.shippingAddress && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2"><FaMapMarkerAlt /> Shipping Address</h3>
                  <p className="text-sm text-gray-700">{selectedOrder.shippingAddress.name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.shippingAddress.address}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.shippingAddress.cityState} - {selectedOrder.shippingAddress.postalCode}</p>
                  {selectedOrder.shippingAddress.mobile && <p className="text-sm text-gray-600 mt-1">Phone: {selectedOrder.shippingAddress.mobile}</p>}
                </div>
              )}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Order Items</h3>
                <div className="space-y-2">
                  {(selectedOrder.orderItems || []).map((item, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <img loading="lazy" src={item.imageUrl || 'https://via.placeholder.com/60'} alt={item.name} className="w-14 h-14 object-cover rounded" onError={e => { e.target.src = 'https://via.placeholder.com/60'; }} />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                        <p className="text-xs text-gray-600">Qty: {item.quantity} × ₹{(item.price || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {(selectedOrder.status === 'Cancelled' && selectedOrder.cancellationReason) && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-sm font-bold text-red-900 mb-1 flex items-center gap-2"><FaTimesCircle /> Cancellation</h4>
                  <p className="text-sm text-gray-800">{selectedOrder.cancellationReason}</p>
                  {selectedOrder.cancelledDate && <p className="text-xs text-gray-600 mt-1">{formatDate(selectedOrder.cancelledDate)}</p>}
                </div>
              )}
              {(selectedOrder.cancelledItems?.length > 0) && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="text-sm font-bold text-amber-900 mb-2">Cancelled Items</h4>
                  {selectedOrder.cancelledItems.map((ci, idx) => (
                    <div key={idx} className="text-sm text-gray-800 mb-1">
                      {ci.name} — Reason: {ci.cancellationReason || '—'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
